<template>
  <!-- Page wrapper -->
  <div class="min-h-screen bg-white text-gray-900">
    <!-- Hero -->
    <section class="relative overflow-hidden">
      <div class="absolute inset-0 -z-10 bg-gradient-to-br from-red-700 via-eurozahn-red to-rose-500 opacity-95"></div>
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div class="max-w-3xl">
          <p class="mb-4 inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-sm leading-6 ring-1 ring-inset ring-white/20 text-black">
            <span class="mr-2 inline-flex h-2 w-2 rounded-full bg-emerald-400"></span>
            Spouštíme 2. ledna 2026
          </p>
          <h1 class="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl text-eurozahn-red">
            <span class="text-eurozahn-gray">EZ</span>Lab: Spolupráce s laboratoří, jakou jste si vždy přáli
          </h1>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Představujeme online portál, který zkracuje administrativu na minuty. Zadáte zakázku, sledujete její stav a komunikujete přímo u dané práce – vše přehledně na jednom místě.
          </p>
          <div class="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="https://ezlab.cz"
              target="_blank"
              rel="noopener"
              class="inline-flex items-center justify-center rounded-xl bg-eurozahn-red px-5 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Prozkoumat výhody na ezlab.cz
            </a>
<!--            <button-->
<!--              type="button"-->
<!--              @click="openLead = true"-->
<!--              class="inline-flex items-center justify-center rounded-xl bg-red-900/30 px-5 py-3 text-base font-semibold text-white ring-1 ring-inset ring-white/20 backdrop-blur-sm transition hover:bg-red-900/40"-->
<!--            >-->
<!--              Domluvit 15min ukázku-->
<!--            </button>-->
          </div>
        </div>
      </div>
    </section>

    <!-- Trust / Stats from Eurozahn -->
    <section aria-labelledby="stats-title" class="border-b border-gray-200 bg-white">
      <h2 id="stats-title" class="sr-only">Důvěryhodnost Eurozahn</h2>
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <dl class="grid grid-cols-2 gap-6 py-8 sm:grid-cols-4">
          <div class="flex flex-col rounded-2xl bg-gray-50 p-4 text-center">
            <dd class="mt-1 text-2xl font-semibold">14+</dd>
            <dt class="text-sm text-gray-600">Let zkušeností</dt>
          </div>
          <div class="flex flex-col rounded-2xl bg-gray-50 p-4 text-center">
            <dd class="mt-1 text-2xl font-semibold">81 000+</dd>
            <dt class="text-sm text-gray-600">Zpracovaných skenů</dt>
          </div>
          <div class="flex flex-col rounded-2xl bg-gray-50 p-4 text-center">
            <dd class="mt-1 text-2xl font-semibold">ČR & SK</dd>
            <dt class="text-sm text-gray-600">Působnost</dt>
          </div>
          <div class="flex flex-col rounded-2xl bg-gray-50 p-4 text-center">
            <dd class="mt-1 text-2xl font-semibold">Po–Pá 8–16</dd>
            <dt class="text-sm text-gray-600">Zákaznická podpora</dt>
          </div>
        </dl>
      </div>
    </section>

    <!-- Value props -->
    <section class="container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
      <div class="mx-auto max-w-2xl text-center">
        <h2 class="text-3xl font-bold tracking-tight sm:text-4xl">Přehled, rychlost a komunikace bez šumu</h2>
        <p class="mt-4 text-lg text-gray-600">
          Tři nejčastější „zloděje času“ vyměňujeme za jasný proces, který šetří nervy i čas.
        </p>
      </div>
      <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:mt-16 lg:grid-cols-3">
        <article
          v-for="feature in features"
          :key="feature.title"
          class="group rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md"
        >
          <div class="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 ring-1 ring-red-100">
            <component :is="feature.icon" class="h-6 w-6 text-red-700" aria-hidden="true" />
          </div>
          <h3 class="mt-4 text-lg font-semibold">{{ feature.title }}</h3>
          <p class="mt-2 text-gray-600">{{ feature.body }}</p>
          <ul v-if="feature.bullets?.length" class="mt-3 space-y-1 text-sm text-gray-600">
            <li v-for="b in feature.bullets" :key="b" class="flex items-start gap-2">
              <CheckCircleIcon class="mt-0.5 h-4 w-4 text-emerald-600" />
              <span>{{ b }}</span>
            </li>
          </ul>
        </article>
      </div>
    </section>

    <!-- How it works -->
    <section class="bg-gray-50 py-16 lg:py-24">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="mx-auto max-w-2xl text-center">
          <h2 class="text-3xl font-bold tracking-tight sm:text-4xl">Od prvního kliknutí po doručení</h2>
          <p class="mt-3 text-lg text-gray-600">
            Bezpapírové zadání, jasný stav práce a veškerá komunikace na jednom místě.
          </p>
        </div>
        <ol class="mx-auto mt-10 grid max-w-4xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <li
            v-for="(s, i) in steps"
            :key="s.title"
            class="relative rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200"
          >
            <div
              class="absolute -top-3 left-6 flex h-10 w-10 items-center justify-center rounded-xl bg-eurozahn-red text-white font-semibold shadow-md"
            >
              {{ i + 1 }}
            </div>
            <h3 class="mt-6 text-lg font-semibold">{{ s.title }}</h3>
            <p class="mt-2 text-sm text-gray-600">{{ s.body }}</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="py-16 lg:py-24">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="relative isolate overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 px-6 py-12 text-center sm:px-12 lg:py-20"
        >
          <h2 class="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Přidejte se k ordinacím, které mají v zakázkách pořádek
          </h2>
          <p class="mx-auto mt-4 max-w-2xl text-lg text-gray-300">
            Zaujala Vás naše vize moderní spolupráce? Domluvte si krátkou konzultaci a zjistěte, jak Vám můžeme usnadnit práci a připravit Vás na výhody aplikace EZLab.
          </p>
          <div class="mt-8 flex flex-col items-center sm:flex-row sm:justify-center gap-4">
            <a
              href="https://ezlab.cz"
              target="_blank"
              rel="noopener"
              class="inline-flex items-center justify-center rounded-xl bg-eurozahn-red px-5 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-red-700"
            >Prozkoumat výhody na ezlab.cz</a
            >
            <a
              href="tel:+420725433894"
              class="inline-flex items-center justify-center rounded-xl bg-gray-200 px-5 py-3 text-base font-semibold text-red-700 shadow-sm transition hover:bg-gray-300"
            >
              Konzultace: +420 725 433 894
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Lead modal -->
    <transition
      enter-active-class="duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="openLead" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
          <div class="flex items-start justify-between">
            <h3 class="text-xl font-semibold">Rychlá poptávka</h3>
            <button @click="openLead = false" class="rounded-full p-2 text-gray-500 hover:bg-gray-100" aria-label="Zavřít">
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <form @submit.prevent="submitLead" class="mt-4 space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700" for="name">Jméno a ordinace</label>
              <input
                id="name"
                v-model="lead.name"
                type="text"
                required
                class="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 shadow-sm focus:border-eurozahn-red focus:outline-none focus:ring-2 focus:ring-eurozahn-red/20"
                placeholder="MUDr. Nováková, Brno"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700" for="email">E-mail</label>
              <input
                id="email"
                v-model="lead.email"
                type="email"
                required
                class="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 shadow-sm focus:border-eurozahn-red focus:outline-none focus:ring-2 focus:ring-eurozahn-red/20"
                placeholder="ordinace@example.cz"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700" for="phone">Telefon</label>
              <input
                id="phone"
                v-model="lead.phone"
                type="tel"
                inputmode="tel"
                class="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 shadow-sm focus:border-eurozahn-red focus:outline-none focus:ring-2 focus:ring-eurozahn-red/20"
                placeholder="+420 123 456 789"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700" for="msg">Poznámka</label>
              <textarea
                id="msg"
                v-model="lead.message"
                rows="3"
                class="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 shadow-sm focus:border-eurozahn-red focus:outline-none focus:ring-2 focus:ring-eurozahn-red/20"
                placeholder="Kdy Vám máme zavolat? Jaké typy prací zadáváte nejčastěji?"
              />
            </div>
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p class="text-xs text-gray-500">
                Odesláním souhlasíte se zpracováním údajů za účelem kontaktování ohledně aplikace EZ Lab.
              </p>
              <button
                type="submit"
                :disabled="submitting"
                class="inline-flex items-center justify-center rounded-xl bg-red-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-800 disabled:opacity-60"
              >
                <span v-if="!submitting">Odeslat</span>
                <span v-else>Odesílám…</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <!-- Floating CTA -->
<!--    <button-->
<!--      @click="openLead = true"-->
<!--      class="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-red-700 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-red-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700"-->
<!--    >-->
<!--      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-5 w-5">-->
<!--        <path-->
<!--          d="M2.25 5.25A.75.75 0 0 1 3 4.5h18a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H3a.75.75 0 0 1-.75-.75V5.25Zm1.5.75v4.5h16.5V6H3.75Zm16.5 6H3.75v6.75H20.25V12.75Z"-->
<!--        />-->
<!--      </svg>-->
<!--      Domluvit ukázku-->
<!--    </button>-->
  </div>
</template>

<script setup>
import { ref } from 'vue'
import {
  ChartBarIcon,
  ChatIcon,
  ClipboardCopyIcon,
  CheckCircleIcon,
} from "@heroicons/vue/outline";

// Data
const features = [
  {
    title: 'Digitální zadávání zakázek',
    body:
      'Intuitivní formulář pohlídá všechny podstatné údaje. Už žádné papírové žádanky a nejistota, zda jste na nic nezapomněli.',
    bullets: ['Přednastavené typy prací a materiálů', 'Nahrání fotografií a STL přímo do zakázky', 'Okamžité potvrzení o přijetí'],
    icon: ClipboardCopyIcon,
  },
  {
    title: 'Okamžitý přehled 24/7',
    body: 'Přehledná nástěnka ukazuje aktuální stav všech zakázek – od přijetí po expedici. Vše dostupné z počítače i z mobilu.',
    bullets: ['Filtrování podle stavu, pacienta nebo termínu', 'Automatické notifikace o důležitých změnách'],
    icon: ChartBarIcon,
  },
  {
    title: 'Komunikace na jednom místě',
    body:
      'Veškeré dotazy a úpravy řešíte v chatu přímo u dané zakázky. Celá historie komunikace je tak snadno a trvale dohledatelná.',
    bullets: ['Konec zdlouhavému hledání v e-mailech', 'Trvalý záznam všech domluvených změn'],
    icon: ChatIcon,
  },
]

const steps = [
  { title: 'Zadáte zakázku', body: 'Vyberete typ práce, materiál a přiložíte potřebné soubory. Inteligentní formulář Vás provede.' },
  { title: 'Máte 100% přehled', body: 'Na nástěnce okamžitě vidíte nový záznam, jeho stav i předpokládaný termín dodání.' },
  { title: 'Komunikujete v kontextu', body: 'Jakékoliv dotazy a připomínky řešíme přímo u zakázky – už žádná nedorozumění.' },
  { title: 'Doručíme v termínu', body: 'O expedici práce se dozvíte díky automatické notifikaci. Vždy víte, na čem jste.' },
]

// UI state
const openLead = ref(false)

// Lead form state
const submitting = ref(false)
const lead = ref({ name: '', email: '', phone: '', message: '' })

async function submitLead() {
  try {
    submitting.value = true
    const res = await fetch('/api/ezlab/lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(lead.value),
    })
    if (!res.ok) throw new Error('Nepodařilo se odeslat formulář')
    openLead.value = false
    lead.value = { name: '', email: '', phone: '', message: '' }
    alert('Děkujeme! Ozveme se co nejdříve.')
  } catch (e) {
    console.error(e)
    alert('Něco se pokazilo. Zkuste to prosím znovu, nebo nám zavolejte.')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.container { max-width: 1200px; }
</style>
